import { ShoppingCartOutlined } from '@ant-design/icons';
import { usePostFrontRichiesteIdPagamentiIdPagamentoOnline } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type PagamentoWithDettaglioDTO } from '@framework/api-client/api/inquiries-compile/schemas/pagamentoWithDettaglioDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { Button, type ButtonProps, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EnumCodStatoPagamento } from '../../burden/payments/EnumCodStatoPagamento.tsx';
import { generatePaymentCallbackUrl } from './StartStampDutyPayment.tsx';

interface StartPaymentButtonProps {
  payment: PagamentoWithDettaglioDTO;
  type?: ButtonProps['type'];
  size?: ButtonProps['size'];
}

export function StartPaymentButton(props: StartPaymentButtonProps) {
  const { payment, type = 'primary', size } = props;

  const { t } = useTranslation();

  const { mutateAsync: startPayment, isLoading } =
    usePostFrontRichiesteIdPagamentiIdPagamentoOnline({
      mutation: {
        onSuccess: response => {
          message.info(
            t('start-payment-button.avvio-pagamento', `Avvio del pagamento...`),
          );
          console.log(`[startPayment.onSuccess`, { response });
        },
        onError: e => {
          message.error(
            getNetworkErrorMessage(
              e,
              t(
                'start-payment-button.errore-avvio-pagamento',
                `Si è verificato un errore durante l'avvio del pagamento`,
              ),
            ),
          );
        },
      },
    });

  const handleClick = useCallback(async () => {
    try {
      const redirectUrl = await startPayment({
        id: payment.idRichiesta!,
        idPagamento: payment.id!,
        data: {
          urlRitorno: generatePaymentCallbackUrl(),
        },
      });

      // Se è stato restituito un URL di redirect, reindirizzo l'utente
      if (redirectUrl && typeof redirectUrl === 'string') {
        window.location.href = redirectUrl;
      } else {
        // biome-ignore format: <tbd>
        console.warn(`[StartPaymentButton] startPayment() returned no redirect URL`, { redirectUrl });
        message.error(
          t(
            'start-payment-button.errore-avvio-pagamento',
            `Si è verificato un errore durante l'avvio del pagamento`,
          ),
        );
      }
    } catch (e) {
      console.error(`[StartPaymentButton] startPayment() failed`, { e });
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'start-payment-button.errore-avvio-pagamento',
            `Si è verificato un errore durante l'avvio del pagamento`,
          ),
        ),
      );
    }
  }, [payment.id, payment.idRichiesta, startPayment, t]);

  if (payment.codStatoPagamento === EnumCodStatoPagamento.SUCCESSO) return null;
  if (!hasPayment(payment)) return null;

  return (
    <Tooltip title={t('form.procedi-al-pagamento', 'Procedi al pagamento')}>
      <Button
        type={type}
        size={size}
        icon={
          <ShoppingCartOutlined
            aria-label={t(
              'form.procedi-al-pagamento-link-esterno',
              'Procedi al pagamento (apre un link esterno)',
            )}
          />
        }
        onClick={handleClick}
        disabled={!payment.id}
        loading={isLoading}
      />
    </Tooltip>
  );
}

function hasPayment(payment: PagamentoWithDettaglioDTO): boolean {
  const hasBurdenPayment =
    payment.oneri?.some(o => o.tipoOnere?.flPagamentoModello1) ?? false;
  const hasQuotePayment =
    payment.preventivo?.tipoPagamento?.flPagamentoModello1 ?? false;
  return hasBurdenPayment || hasQuotePayment;
}
